"use client";

import React, { useEffect, useRef, useState } from "react";
import { Map, Compass, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function TopoMap() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    if (!svgRef.current) return;
    const handleMouseMove = (event: MouseEvent) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        setMousePos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        });
      }
    };
    const svg = svgRef.current;
    svg.addEventListener('mousemove', handleMouseMove);
    return () => {
      svg.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Generate contour paths using pure SVG/JavaScript
  const generateBlobPath = (centerX: number, centerY: number, radius: number, noise: number, segments: number = 20) => {
    const points: string[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = i / segments * Math.PI * 2;
      const r = radius + (Math.random() - 0.5) * noise;
      const x = Math.cos(angle) * r + centerX;
      const y = Math.sin(angle) * r + centerY;
      if (i === 0) {
        points.push(`M ${x} ${y}`);
      } else {
        points.push(`L ${x} ${y}`);
      }
    }
    points.push('Z');
    return points.join(' ');
  };
  const layers = 12;
  const baseRadius = 100;
  const radiusStep = 35;
  const width = 800;
  const height = 800;
  const centerX = width / 2;
  const centerY = height / 2;

  // Generate contour data
  const contours = Array.from({
    length: layers
  }, (_, i) => ({
    id: i,
    path: generateBlobPath(centerX, centerY, baseRadius + i * radiusStep, 40 + i * 15),
    opacity: 0.1 + i / layers * 0.4
  }));
  return <div className="absolute inset-0 w-full h-full bg-[hsl(220,15%,10%)] overflow-hidden">
      <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid slice" className="w-full h-full opacity-60">
        {contours.map((contour, i) => {
        const factor = (i + 1) * 0.5;
        const moveX = (mousePos.x - centerX) * 0.01 * factor;
        const moveY = (mousePos.y - centerY) * 0.01 * factor;
        return <path key={contour.id} d={contour.path} fill="none" stroke={`hsla(35, 30%, 88%, ${contour.opacity})`} strokeWidth="1.5" style={{
          transform: `translate(${moveX}px, ${moveY}px)`,
          transition: 'transform 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
          animation: `drawPath 3s cubic-bezier(0.4, 0.0, 0.2, 1) ${i * 200}ms forwards`
        }} />;
      })}
      </svg>
      
      <style>{`
        @keyframes drawPath {
          from {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
          }
          to {
            stroke-dasharray: 1000;
            stroke-dashoffset: 0;
          }
        }
      `}</style>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[hsl(220,15%,10%)]/80 via-transparent to-transparent pointer-events-none" />
    </div>;
}
export default function TravelLog() {
  return <section id="travel-log" className="dark bg-background text-foreground w-full min-h-[800px] flex flex-col lg:flex-row relative overflow-hidden">
      {/* Left side: Topographic Map Visual */}
      <div className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-auto border-b lg:border-b-0 lg:border-r border-[hsl(35,30%,88%)]/10">
        <TopoMap />
        
        <div className="absolute bottom-8 left-8 z-10 max-w-md">
          <h3 className="text-[hsl(35,30%,88%)] font-serif text-4xl tracking-wide drop-shadow-md">
            SIERRA NEVADA
          </h3>
          <p className="text-[hsl(35,30%,88%)]/70 font-mono text-sm mt-2">
            ELEVATION: 14,505 FT <br />
            COORDINATES: 36.5785° N, 118.2923° W
          </p>
        </div>
      </div>

      {/* Right side: Content panel */}
      <div className="w-full lg:w-1/2 relative flex flex-col justify-center p-8 lg:p-20 bg-[hsl(35,30%,88%)] text-[hsl(220,15%,10%)]">
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-multiply"></div>

        <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(220,15%,10%)]/20 bg-[hsl(220,15%,10%)]/5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[hsl(15,60%,50%)] animate-pulse"></span>
            <span className="text-xs font-bold tracking-wider uppercase text-[hsl(220,15%,10%)]/70">
              Start Here
            </span>
          </div>

          <h2 className="text-5xl md:text-6xl font-serif font-bold tracking-tight mb-6 text-[hsl(220,15%,10%)]">
            NEW TO THE TRAIL?
          </h2>

          <p className="text-lg md:text-xl leading-relaxed text-[hsl(220,15%,10%)]/80 mb-10 font-sans">
            Welcome to Wildlings West Overland. Our mission is to help families explore the American West with confidence. 
            Whether you're building your first rig or planning a weekend escape, we've mapped out the journey for you.
          </p>

          {/* Navigation Cards */}
          <div className="space-y-4">
            {/* Card 1: Gearing Up */}
            <div className="group relative pl-8 border-l-2 border-[hsl(220,15%,10%)]/10 hover:border-[hsl(15,60%,50%)] transition-colors duration-300">
              <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[hsl(35,30%,88%)] border-2 border-[hsl(220,15%,10%)]/30 group-hover:border-[hsl(15,60%,50%)] transition-colors duration-300"></div>
              <Link to="/TheRigsPage" className="block w-full">
                <Button variant="ghost" className="w-full justify-between h-auto py-4 px-4 bg-[hsl(220,15%,10%)]/5 hover:bg-[hsl(220,15%,10%)]/10 text-[hsl(220,15%,10%)] border border-transparent hover:border-[hsl(220,15%,10%)]/10 rounded-lg transition-all">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[hsl(220,15%,10%)]/10 rounded-md text-[hsl(220,15%,10%)]">
                      <Map className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-lg">Gearing Up</div>
                      <div className="text-sm opacity-70 font-normal">Vehicle builds & essentials</div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" />
                </Button>
              </Link>
            </div>

            {/* Card 2: Choosing a Route */}
            <div className="group relative pl-8 border-l-2 border-[hsl(220,15%,10%)]/10 hover:border-[hsl(15,60%,50%)] transition-colors duration-300">
              <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[hsl(35,30%,88%)] border-2 border-[hsl(220,15%,10%)]/30 group-hover:border-[hsl(15,60%,50%)] transition-colors duration-300"></div>
              <Link to="/TravelLogPage" className="block w-full">
                <Button variant="ghost" className="w-full justify-between h-auto py-4 px-4 bg-[hsl(220,15%,10%)]/5 hover:bg-[hsl(220,15%,10%)]/10 text-[hsl(220,15%,10%)] border border-transparent hover:border-[hsl(220,15%,10%)]/10 rounded-lg transition-all">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[hsl(220,15%,10%)]/10 rounded-md text-[hsl(220,15%,10%)]">
                      <Compass className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-lg">Choosing a Route</div>
                      <div className="text-sm opacity-70 font-normal">Trip logs & destinations</div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" />
                </Button>
              </Link>
            </div>

            {/* Card 3: Safety First */}
            <div className="group relative pl-8 border-l-2 border-[hsl(220,15%,10%)]/10 hover:border-[hsl(15,60%,50%)] transition-colors duration-300">
              <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[hsl(35,30%,88%)] border-2 border-[hsl(220,15%,10%)]/30 group-hover:border-[hsl(15,60%,50%)] transition-colors duration-300"></div>
              <Link to="/OutAndBackPage" className="block w-full">
                <Button variant="ghost" className="w-full justify-between h-auto py-4 px-4 bg-[hsl(220,15%,10%)]/5 hover:bg-[hsl(220,15%,10%)]/10 text-[hsl(220,15%,10%)] border border-transparent hover:border-[hsl(220,15%,10%)]/10 rounded-lg transition-all">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[hsl(220,15%,10%)]/10 rounded-md text-[hsl(220,15%,10%)]">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-lg">Safety First</div>
                      <div className="text-sm opacity-70 font-normal">Gear reviews & guides</div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" />
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>;
}